import React, { useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { PlayerStatus } from '../hooks/useThemePlayer';
type ThemeButtonProps = {
  status: PlayerStatus;
  progress: number;
  fallbackUrl: string;
  onPress: () => void;
  onStop: () => void;
};
const TRIPLE_TAP_WINDOW_MS = 600;
const spring = {
  snappy: {
    type: 'spring',
    stiffness: 500,
    damping: 30,
    mass: 0.8
  },
  bouncy: {
    type: 'spring',
    stiffness: 400,
    damping: 18,
    mass: 1
  }
} as const;
const RING_RADIUS = 47;
const CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const captions: Record<PlayerStatus, string> = {
  ready: 'tap to check in',
  playing: 'triple-tap to stop',
  error: 'open on YouTube'
};
export function ThemeButton({
  status,
  progress,
  fallbackUrl,
  onPress,
  onStop
}: ThemeButtonProps) {
  const reduce = useReducedMotion();
  const [taps, setTaps] = useState(0);
  const tapTimesRef = useRef<number[]>([]);
  const handlePress = () => {
    if (status === 'error') {
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const now = Date.now();
    const recent = [...tapTimesRef.current, now].filter(t => now - t < TRIPLE_TAP_WINDOW_MS);
    setTaps(t => t + 1);
    if (recent.length >= 3) {
      tapTimesRef.current = [];
      onStop();
      return;
    }
    tapTimesRef.current = recent;
    onPress();
  };
  return <div className="relative grid place-items-center" data-id="element-11" data-mp="3f8ex0ssfdm7l">
      {/* Song progress ring */}
      <svg viewBox="0 0 100 100" className="pointer-events-none absolute h-[272px] w-[272px] -rotate-90 md:h-[328px] md:w-[328px]" aria-hidden="true" data-id="element-12" data-mp="3fa971c25rtww">
        <circle cx="50" cy="50" r={RING_RADIUS} fill="none" stroke="#F6EAD3" strokeOpacity="0.18" strokeWidth="1.5" data-id="element-13" data-mp="36e9xlkz872gg" />
        <circle cx="50" cy="50" r={RING_RADIUS} fill="none" stroke="#E9877A" strokeWidth="2" strokeLinecap="round" strokeDasharray={CIRCUMFERENCE} strokeDashoffset={CIRCUMFERENCE * (1 - progress)} style={{
        opacity: status === 'playing' || progress > 0 ? 1 : 0,
        transition: 'opacity 200ms ease-out'
      }} data-id="element-14" data-mp="31f73uwop4ins" />
      </svg>

      {/* One-shot ripple per tap */}
      <AnimatePresence data-id="element-15" data-mp="83ge3e079sg6">
        {!reduce && taps > 0 && <motion.span key={taps} aria-hidden="true" className="pointer-events-none absolute h-56 w-56 rounded-full border-2 border-cream md:h-64 md:w-64" initial={{
        scale: 1,
        opacity: 0.6
      }} animate={{
        scale: 1.22,
        opacity: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-16" data-mp="c5bgymxs4dw2" />}
      </AnimatePresence>

      <motion.button type="button" onClick={handlePress} aria-label={status === 'error' ? 'Open The White Lotus theme on YouTube' : 'Play The White Lotus theme from the beginning'} initial={reduce ? false : {
      scale: 0.96,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} whileHover={reduce ? undefined : {
      scale: 1.04
    }} whileTap={reduce ? undefined : {
      scale: 0.95
    }} transition={spring.bouncy} className="relative flex h-56 w-56 flex-col items-center justify-center rounded-full bg-cream text-jungle shadow-[0_10px_0_#0C2219,0_24px_48px_rgba(12,34,25,0.55)] outline-none focus-visible:ring-4 focus-visible:ring-hibiscus focus-visible:ring-offset-4 focus-visible:ring-offset-jungle md:h-64 md:w-64" data-id="element-17" data-mp="3pcvd9n9th5bx">
        <motion.span key={`word-${taps}`} initial={reduce || taps === 0 ? false : {
        scale: 0.9
      }} animate={{
        scale: 1
      }} transition={spring.bouncy} className="block text-[52px] font-bold leading-none tracking-[-0.02em] md:text-[60px]" data-id="element-18" data-mp="3mqvpszphr05y">
          Aloha!
        </motion.span>
        <span className="relative mt-3 block h-5 w-full overflow-hidden text-[14px] font-semibold text-jungle/70" data-id="element-19" data-mp="26dxfqzcbm1kh">
          <AnimatePresence mode="popLayout" initial={false} data-id="element-20" data-mp="21gyp4qrv8cr1">
            <motion.span key={status} className="absolute inset-0 whitespace-nowrap" initial={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -12
          }} transition={reduce ? {
            duration: 0.15
          } : spring.snappy} data-id="element-21" data-mp="23shbdvtdsqu4">
              {captions[status]}
            </motion.span>
          </AnimatePresence>
        </span>
      </motion.button>

      <span className="sr-only" aria-live="polite" data-id="element-22" data-mp="1p4w73ykrslkm">
        {status === 'playing' ? 'Now playing The White Lotus theme' : status === 'error' ? 'Playback unavailable' : ''}
      </span>
    </div>;
}